import FormOptionCard, {
  FormOptionCardConfig,
} from "app/components/FormOptionCard";
import React from "react";
import { useDispatch } from "react-redux";

import {
  QuoteSafetyAndStandardsCard,
  WING_LOCATION_OPTIONS,
  WING_MATERIAL_OPTIONS,
} from "../../../types";
import {
  safetyKey,
  safetyLabels,
  useQuoteAccessories,
} from "../../../hooks/useQuoteAccessories";
import { quotesActions } from "../../../redux/slice";
import {
  CardRow,
  FieldsRow,
  StructureCard,
  StructureSection,
} from "./BoxStructureFields";
import { NumberField, SelectField } from "./FormFields";

const noteField = (titleKey: string): FormOptionCardConfig => ({
  titleKey,
  fields: ["note"],
  notePlaceholderKey: safetyKey("FIELDS.NOTE"),
});

const safetyAndStandardsCards: Record<
  QuoteSafetyAndStandardsCard,
  FormOptionCardConfig
> = {
  rearBumper: noteField(safetyKey("CARDS.REAR_BUMPER")),
  hazardousMaterialsMounts: noteField(
    safetyKey("CARDS.HAZARDOUS_MATERIALS_MOUNTS")
  ),
  spareWheelCarrier: {
    titleKey: safetyKey("CARDS.SPARE_WHEEL_CARRIER"),
    fields: ["radio"],
    radioOptions: [
      { value: "REGULAR", labelKey: safetyKey("OPTIONS.REGULAR") },
      { value: "BASKET", labelKey: safetyKey("OPTIONS.BASKET") },
    ],
  },
  rearLightMounts: {
    titleKey: safetyKey("CARDS.REAR_LIGHT_MOUNTS"),
    fields: ["subCheckbox"],
    subCheckboxLabelKey: safetyKey("FIELDS.LIGHT_PROTECTION"),
  },
  extinguisherMounts: noteField(safetyKey("CARDS.EXTINGUISHER_MOUNTS")),
  couplingLocks: noteField(safetyKey("CARDS.COUPLING_LOCKS")),
};

const roundIronSidesCard: FormOptionCardConfig = {
  titleKey: safetyKey("CARDS.ROUND_IRON_SIDES"),
  fields: ["size"],
  sizeLabelKey: safetyKey("FIELDS.ROUND_IRON_DIAMETER"),
};

const WingsCard = () => {
  const dispatch = useDispatch();
  const data = useQuoteAccessories();
  const wings = data.vehicleAddons.wings;

  return (
    <StructureCard
      enabled={wings.enabled}
      titleKey={safetyKey("CARDS.WINGS")}
      onToggle={(enabled) =>
        dispatch(
          quotesActions.updateAccessories({
            section: "vehicleAddons",
            key: "wings",
            value: { enabled },
          })
        )
      }
    >
      <FieldsRow>
        <SelectField
          labelKey={safetyKey("FIELDS.MATERIAL")}
          value={wings.itemType}
          options={WING_MATERIAL_OPTIONS}
          onChange={(itemType) =>
            dispatch(
              quotesActions.updateAccessories({
                section: "vehicleAddons",
                key: "wings",
                value: { itemType },
              })
            )
          }
          grow
        />
        <SelectField
          labelKey={safetyKey("FIELDS.LOCATION")}
          value={wings.location}
          options={WING_LOCATION_OPTIONS}
          onChange={(location) =>
            dispatch(
              quotesActions.updateAccessories({
                section: "vehicleAddons",
                key: "wings",
                value: { location },
              })
            )
          }
          grow
        />
        <NumberField
          labelKey={safetyKey("FIELDS.QUANTITY")}
          value={wings.quantity}
          onChange={(quantity) =>
            dispatch(
              quotesActions.updateAccessories({
                section: "vehicleAddons",
                key: "wings",
                value: { quantity },
              })
            )
          }
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};

const AccessoriesSafety = () => {
  const dispatch = useDispatch();
  const data = useQuoteAccessories();

  const renderCard = (cardId: QuoteSafetyAndStandardsCard) => (
    <FormOptionCard
      key={cardId}
      config={safetyAndStandardsCards[cardId]}
      value={data.safetyAndStandards[cardId]}
      labels={safetyLabels}
      onChange={(value) =>
        dispatch(
          quotesActions.updateAccessories({
            section: "safetyAndStandards",
            key: cardId,
            value,
          })
        )
      }
    />
  );

  return (
    <>
      <StructureSection titleKey={safetyKey("SECTIONS.SAFETY")}>
        <CardRow columns={3}>
          {renderCard("rearBumper")}
          {renderCard("hazardousMaterialsMounts")}
          {renderCard("spareWheelCarrier")}
        </CardRow>
        <CardRow columns={3}>
          {renderCard("rearLightMounts")}
          {renderCard("extinguisherMounts")}
          {renderCard("couplingLocks")}
        </CardRow>
      </StructureSection>

      <StructureSection titleKey={safetyKey("SECTIONS.VEHICLE_ADDONS")}>
        <CardRow columns={1}>
          <WingsCard />
        </CardRow>
        <CardRow columns={1}>
          <FormOptionCard
            config={roundIronSidesCard}
            value={data.vehicleAddons.roundIronSides}
            labels={safetyLabels}
            onChange={(value) =>
              dispatch(
                quotesActions.updateAccessories({
                  section: "vehicleAddons",
                  key: "roundIronSides",
                  value,
                })
              )
            }
          />
        </CardRow>
      </StructureSection>
    </>
  );
};

export default AccessoriesSafety;
